/* eslint-disable prettier/prettier */
import {
  Grid,
  TextField,
  Box,
  Button,
  Typography,
} from "@material-ui/core";
import { useFormik } from "formik";
import { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import * as Yup from "yup";
import Alert from "@material-ui/lab/Alert";
import { useAppContext } from "../../api/AppContext";
import {
  ensurePromptMultilingualObject,
  formatLanguageList,
  getCompletionState,
  getEditableLanguagesForPromptSection,
  getIncompleteLanguages,
  getMandatoryLanguagesForPromptSection,
  getPreferredLanguageFromUser,
  getValidLanguagesFromUser,
  isTagNameCompleteForSave,
  LANGUAGE_LABELS,
  requiresAllMandatoryLanguages,
} from "../shared/languageConfig";
import { LanguageChips, PinnedLanguageField } from "../shared/LanguageSelector";
import { usePreferredLanguagePersistence } from "../shared/usePreferredLanguagePersistence";

const Tags = ({
  basePath,
  formik,
  preferredLanguage,
  setPreferredLanguage,
  validLanguages,
}) => {
  const handleInputChange = (name, value, lang) => {
    if (lang) {
      formik.setFieldValue(name, {
        ...formik.values[name],
        [lang]: value,
      });
    } else {
      formik.setFieldValue(name, value);
    }
  };

  const secondaryLanguages = validLanguages.filter(
    (language) => language !== preferredLanguage
  );
  const [selectedPreferredLanguage, setSelectedPreferredLanguage] = useState(
    preferredLanguage
  );
  const [activeSecondaryLanguage, setActiveSecondaryLanguage] = useState(
    secondaryLanguages[0] || null
  );
  const persistPreferredLanguage = usePreferredLanguagePersistence();

  const handleConfirmPreferredLanguage = async () => {
    setPreferredLanguage(selectedPreferredLanguage);
    await persistPreferredLanguage(selectedPreferredLanguage);
  };

  useEffect(() => {
    setSelectedPreferredLanguage(preferredLanguage);
  }, [preferredLanguage]);

  useEffect(() => {
    if (!secondaryLanguages.length) {
      setActiveSecondaryLanguage(null);
      return;
    }

    if (!secondaryLanguages.includes(activeSecondaryLanguage)) {
      setActiveSecondaryLanguage(secondaryLanguages[0]);
    }
  }, [activeSecondaryLanguage, secondaryLanguages]);

  const incompleteMandatoryLanguages = useMemo(() => {
    if (!requiresAllMandatoryLanguages(basePath)) {
      return [];
    }

    return getIncompleteLanguages(
      getMandatoryLanguagesForPromptSection(basePath),
      (language) =>
        isTagNameCompleteForSave(formik.values.tagname, language)
    );
  }, [basePath, formik.values.tagname]);

  const missingMandatoryLanguagesMessage = useMemo(() => {
    if (formik.submitCount === 0 || !incompleteMandatoryLanguages.length) {
      return null;
    }

    return `Saving requires these languages to be fully filled: ${formatLanguageList(
      incompleteMandatoryLanguages
    )}. Switch language chips to complete the missing translations.`;
  }, [formik.submitCount, incompleteMandatoryLanguages]);

  const languageCompletionByCode = useMemo(() => {
    return validLanguages.reduce((acc, language) => {
      const completed = formik.values.tagname?.[language]?.trim() ? 1 : 0;

      acc[language] = getCompletionState({
        completedLanguage: completed,
        isActive: language === activeSecondaryLanguage,
        totalRequiredFields: 1,
      });
      return acc;
    }, {});
  }, [activeSecondaryLanguage, formik.values.tagname, validLanguages]);

  const renderLanguageColumn = (language) => {
    if (!language) {
      return null;
    }

    const languageLabel = LANGUAGE_LABELS[language] || language.toUpperCase();
    const hasAtLeastOneCompleteName = validLanguages.some((lang) =>
      isTagNameCompleteForSave(formik.values.tagname, lang)
    );
    const showAtLeastOneFieldHint =
      !requiresAllMandatoryLanguages(basePath) &&
      formik.submitCount > 0 &&
      !hasAtLeastOneCompleteName &&
      language === preferredLanguage &&
      !formik.values.tagname?.[language]?.trim();

    const showNameError =
      ((formik.touched.tagname?.[language] || formik.submitCount > 0) &&
        Boolean(formik.errors.tagname?.[language])) ||
      showAtLeastOneFieldHint;

    const nameHelperText = formik.errors.tagname?.[language]
      ? formik.touched.tagname?.[language] || formik.submitCount > 0
        ? formik.errors.tagname[language]
        : undefined
      : showAtLeastOneFieldHint
      ? "Name is mandatory"
      : undefined;

    return (
      <Grid item xs={12}>
        <TextField
          fullWidth
          InputLabelProps={{ shrink: true }}
          error={showNameError}
          helperText={nameHelperText}
          label={`Name (${languageLabel})`}
          margin="none"
          value={formik.values.tagname?.[language] || ""}
          variant="outlined"
          onBlur={() => formik.setFieldTouched(`tagname.${language}`, true)}
          onChange={(event) =>
            handleInputChange("tagname", event.target.value, language)
          }
        />
      </Grid>
    );
  };

  return (
    <Box
      alignContent="center"
      boxSizing="border-box"
      display="flex"
      flexDirection="column"
      height="100%"
      p={4}
      width="100%"
    >
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item md={secondaryLanguages.length ? 6 : 12} xs={12}>
            <Box mb={2} style={{ minHeight: 40 }}>
              <PinnedLanguageField
                requireSetConfirmation
                showInlineLabel
                inlineLabel="Select default language"
                validLanguages={validLanguages}
                value={selectedPreferredLanguage}
                onChange={setSelectedPreferredLanguage}
                onConfirm={handleConfirmPreferredLanguage}
              />
            </Box>
            <Grid container spacing={2}>
              {renderLanguageColumn(preferredLanguage)}
            </Grid>
          </Grid>
          {secondaryLanguages.length > 0 && (
            <Grid item md={6} xs={12}>
              <Box mb={2} style={{ minHeight: 40 }}>
                <LanguageChips
                  languageCompletionByCode={languageCompletionByCode}
                  languages={secondaryLanguages}
                  onLanguageClick={setActiveSecondaryLanguage}
                />
              </Box>
              <Grid container spacing={2}>
                {renderLanguageColumn(activeSecondaryLanguage)}
              </Grid>
            </Grid>
          )}

          <Grid container item justifyContent="center" mt={2} xs={12}>
            <Typography align="center" color="textSecondary">
              Color
            </Typography>
            <input
              style={{ marginLeft: "0.5rem" }}
              type="color"
              value={formik.values.tagcolor}
              onChange={(event) =>
                handleInputChange("tagcolor", event.target.value)
              }
            />
          </Grid>
        </Grid>

        <Box alignItems="center" display="flex" flexDirection="column" mt={2}>
          {missingMandatoryLanguagesMessage && (
            <Box maxWidth={640} mb={2} width="100%">
              <Alert severity="warning">
                {missingMandatoryLanguagesMessage}
              </Alert>
            </Box>
          )}
          <Button
            color="primary"
            type="submit"
            variant="contained"
            onClick={formik.handleSubmit}
          >
            Submit
          </Button>
        </Box>
      </Grid>
    </Box>
  );
};

const TagsContainer = () => {
  const { updateTag, user } = useAppContext();
  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();
  const [configurationEntity, setConfigurationEntity] = useState(null);
  const { rowData } = location.state || {};
  const basePath = useMemo(
    () => location.pathname.split("/")[1],
    [location.pathname]
  );
  const validLanguages = useMemo(() => getValidLanguagesFromUser(user), [user]);
  const editableLanguages = useMemo(
    () => getEditableLanguagesForPromptSection(basePath, validLanguages),
    [basePath, validLanguages]
  );
  const [preferredLanguage, setPreferredLanguage] = useState(() =>
    getPreferredLanguageFromUser(user, editableLanguages)
  );

  useEffect(() => {
    setPreferredLanguage(getPreferredLanguageFromUser(user, editableLanguages));
  }, [user, editableLanguages]);

  useEffect(() => {
    if (rowData) {
      setConfigurationEntity({
        ...rowData,
        id,
        tagname: ensurePromptMultilingualObject(rowData.tagname),
      });
    }
  }, [location.state, id, rowData]);

  const validationSchema = useMemo(() => {
    if (requiresAllMandatoryLanguages(basePath)) {
      const mandatoryLanguages = getMandatoryLanguagesForPromptSection(basePath);

      return Yup.object({
        tagcolor: Yup.string().required("Color is required"),
        tagname: Yup.object().shape(
          mandatoryLanguages.reduce((shape, language) => {
            shape[language] = Yup.string().trim().required("Name is mandatory");
            return shape;
          }, {})
        ),
      });
    }

    return Yup.object({
      tagcolor: Yup.string().required("Color is required"),
      tagname: Yup.object(),
    }).test(
      "at-least-one-language-tagname",
      "At least one language must have a name",
      (value) =>
        editableLanguages.some((language) =>
          isTagNameCompleteForSave(value?.tagname, language)
        )
    );
  }, [basePath, editableLanguages]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: configurationEntity || {
      isNew: true,
      tagcolor: "",
      tagname: ensurePromptMultilingualObject(),
    },
    onSubmit: async (values) => {
      await updateTag(
        {
          ...values,
          tagname: ensurePromptMultilingualObject(values.tagname),
        },
        basePath
      );
      history.push(`/${basePath}/tags`);
    },
    validationSchema,
    validator: () => ({}),
  });

  if (!rowData && !formik.values.isNew) {
    return (
      <Box
        alignItems="center"
        display="flex"
        justifyContent="center"
        width="100%"
      >
        <Typography variant="subtitle1">Tag is not found 😢</Typography>
      </Box>
    );
  }

  return (
    <Tags
      basePath={basePath}
      formik={formik}
      preferredLanguage={preferredLanguage}
      setPreferredLanguage={setPreferredLanguage}
      validLanguages={editableLanguages}
    />
  );
};

export default TagsContainer;
